/**
 * Generic client-side sort utility for tasks
 */

export interface SortState {
  field: string;
  direction: 'asc' | 'desc';
}

/**
 * Sort items by a field using accessor functions.
 *
 * @param items - Items to sort (not mutated; returns a new array)
 * @param sort - Active sort state (field + direction)
 * @param fieldAccessors - Map of field name → accessor that returns a sortable value
 */
export function sortItems<T>(
  items: T[],
  sort: SortState | null,
  fieldAccessors: Record<string, (item: T) => string | number | null>,
): T[] {
  if (!sort) return items;

  const accessor = fieldAccessors[sort.field];
  if (!accessor) return items;

  const multiplier = sort.direction === 'asc' ? 1 : -1;

  return [...items].sort((a, b) => {
    const aVal = accessor(a);
    const bVal = accessor(b);

    // Nulls always sort to the end regardless of direction
    if (aVal == null && bVal == null) return 0;
    if (aVal == null) return 1;
    if (bVal == null) return -1;

    if (typeof aVal === 'string' && typeof bVal === 'string') {
      return aVal.localeCompare(bVal) * multiplier;
    }

    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return (aVal - bVal) * multiplier;
    }

    // Mixed types: compare as strings
    return String(aVal).localeCompare(String(bVal)) * multiplier;
  });
}
